import * as React from 'react';

import {
  maybeParseCalendarDate,
  toISODateString,
} from '@rematter/common/dateTime';
import {
  enumBookingStatus,
  enumSalesOrderStatus,
  SalesOrderLineItem,
} from '@rematter/frontend-lib-api-client';
import { ContentSection, TextField, cn } from '@rematter/ui-web';
import { Grid, SmartDatePicker } from '@rematter/ui-web/components';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import {
  $loadCreateForm,
  $loadCreateSalesOrderId,
  $remoteLoadCreateSalesOrderLineItems,
} from '~/modules/trade/_state/loadCreateForm.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { mapSalesOrderLineItems } from '~/modules/trade/utils/loadCreate';
import { SelectBooking } from '~/shared/components/select-fields/SelectBooking';
import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';
import {
  SelectableSalesOrder,
  SelectSalesOrder,
} from '~/shared/components/select-fields/SelectSalesOrder';

type LoadCreateFormProps = {
  className?: string;
  requiredBookingId?: string;
  requiredSalesOrderId?: string;
};

export const LoadCreateForm = ({
  className,
  requiredBookingId,
  requiredSalesOrderId,
}: LoadCreateFormProps) => {
  const t = useTranslations();

  const [form, updateForm] = useAtom($loadCreateForm);

  const updateLoadCreateSalesOrderId = useSetAtom($loadCreateSalesOrderId);

  const lineItemsQuery = useAtomValue($remoteLoadCreateSalesOrderLineItems);

  // 🟡🟡🟡 Track previous salesOrderId to prevent unnecessary updates
  const previousSalesOrderIdRef = React.useRef<string | undefined>(undefined);

  const handleSalesOrderSelectChange = (
    value?: string,
    salesOrder?: SelectableSalesOrder,
  ) => {
    updateLoadCreateSalesOrderId(value?.toString());
    updateForm({
      ...form,
      salesOrderNumber: salesOrder?.documentNumber ?? undefined,
      salesOrderStatus: salesOrder?.status ?? undefined,
      salesOrderId: value?.toString(),
    });
  };

  const handleDateChange = (value: string | null) => {
    updateForm({
      ...form,
      expectedShipDate: value ? maybeParseCalendarDate(value) : undefined,
    });
  };

  const handleChangeFieldValue =
    (field: keyof typeof form) => (value: string | undefined) => {
      const fieldValue = value?.toString() || undefined;

      updateForm({
        ...form,
        [field]: fieldValue,
      });
    };

  React.useEffect(() => {
    if (requiredSalesOrderId) {
      updateForm((f) => ({ ...f, salesOrderId: requiredSalesOrderId }));
      updateLoadCreateSalesOrderId(requiredSalesOrderId);
    }
    if (requiredBookingId) {
      updateForm((f) => ({ ...f, bookingId: requiredBookingId }));
    }
  }, [
    requiredSalesOrderId,
    requiredBookingId,
    updateForm,
    updateLoadCreateSalesOrderId,
  ]);

  // 🟡🟡🟡 Sync line items when selecting a new sales order
  React.useEffect(() => {
    const currentSalesOrderId = form.salesOrderId;

    if (!currentSalesOrderId) {
      if (previousSalesOrderIdRef.current !== undefined) {
        updateForm((prevForm) => ({
          ...prevForm,
          lineItems: [],
        }));
        previousSalesOrderIdRef.current = undefined;
      }
      return;
    }

    if (
      currentSalesOrderId === previousSalesOrderIdRef.current ||
      lineItemsQuery.isLoading ||
      !lineItemsQuery.data
    ) {
      return;
    }

    const entry = lineItemsQuery.data.salesOrders.entries[0];
    if (entry?.lineItems) {
      const lineItems = entry.lineItems as SalesOrderLineItem[];
      const transformedLineItems = mapSalesOrderLineItems(lineItems);

      updateForm((prevForm) => ({
        ...prevForm,
        lineItems: transformedLineItems,
      }));

      previousSalesOrderIdRef.current = currentSalesOrderId;
    }
  }, [
    form.salesOrderId,
    lineItemsQuery.data,
    lineItemsQuery.isLoading,
    updateForm,
  ]);

  const bookingValidStatus = React.useMemo(() => [enumBookingStatus.Open], []);

  return (
    <ContentSection
      className={cn('@LoadCreateForm', '!border-t-0 !pt-0', className)}
    >
      <Grid rows4 gap3>
        <TextField
          label={t.loadsView.form.documentNumber}
          value={form.loadNumber || ''}
          onChange={(value) =>
            updateForm({ ...form, loadNumber: value || undefined })
          }
          placeholder={t.common.placeholders.generateNumber}
        />
        <SmartDatePicker
          value={form.expectedShipDate}
          label={t.loadsView.form.expectedShipDate}
          onChange={(date) =>
            handleDateChange(date ? toISODateString(date) : null)
          }
          isRequired={true}
        />
        <SelectFacility
          value={form.facilityId}
          label={t.loadsView.form.facilityLocationId}
          onChange={handleChangeFieldValue('facilityId')}
          isRequired={true}
          autoSelectFirstOption
        />
        <SelectSalesOrder
          value={requiredSalesOrderId ?? form.salesOrderId}
          isDisabled={Boolean(requiredSalesOrderId)}
          isRequired={Boolean(requiredSalesOrderId)}
          label={t.loadsView.form.salesOrderId}
          onChange={handleSalesOrderSelectChange}
          statuses={[enumSalesOrderStatus.Open]}
        />
        <SelectBooking
          value={requiredBookingId ?? form.bookingId}
          isDisabled={Boolean(requiredBookingId)}
          isRequired={Boolean(requiredBookingId)}
          label={t.loadsView.form.bookingId}
          statuses={bookingValidStatus}
          onChange={handleChangeFieldValue('bookingId')}
        />
      </Grid>
    </ContentSection>
  );
};
